/**
 * Unified Early Bird + Surge pricing contract (frontend-only, Phase 1).
 * All new backend keys are optional. UI reads via parseUnifiedOffer so that
 * when backend ships real keys only this adapter changes.
 */

import { schedule_types_ids } from "features/Listings/constants/Listings.constants";
import {
  DEFAULT_EVERGREEN_START_EVENT,
  DEFAULT_THRESHOLD_UNIT,
  DEFAULT_VALUE_UNIT,
  DEFAULT_VIP_APPLY_BY,
  EVERGREEN_START_EVENT,
  THRESHOLD_UNIT,
} from "../constants";

// Direction chosen in Step-0 chooser. Default = discount (existing behaviour).
export const OFFER_DIRECTION = {
  DISCOUNT: "discount",
  SURGE: "surge",
};

export const TRIGGER_TYPE = {
  SCHEDULE: "schedule",
  EVERGREEN: "evergreen",
  SLOTS_FILLED: "slots_filled",
  SLOTS_LEFT: "slots_left",
  ENROLLMENT_FILLED: "enrollment_filled",
  SEATS_FILLED: "seats_filled",
  SEATS_LEFT: "seats_left",
  STOCK_SOLD: "stock_sold",
  STOCK_LEFT: "stock_left",
  MEMBER_COUNT: "member_count",
  SPOTS_LEFT: "spots_left",
  TIME_TO_SLOT: "time_to_slot",
  TIME_TO_EVENT: "time_to_event",
  TIME_TO_COHORT_START: "time_to_cohort_start",
  TIME_TO_BATCH_START: "time_to_batch_start",
  VIP_SLOTS: "vip_slots",
  BATCH_WISE: "batch_wise",
};

export const TRIGGER_LABELS = {
  [TRIGGER_TYPE.SCHEDULE]: "Schedule",
  [TRIGGER_TYPE.EVERGREEN]: "Evergreen",
  [TRIGGER_TYPE.SLOTS_FILLED]: "Slots Filled",
  [TRIGGER_TYPE.SLOTS_LEFT]: "Slots Left",
  [TRIGGER_TYPE.ENROLLMENT_FILLED]: "Enrollment Filled",
  [TRIGGER_TYPE.SEATS_FILLED]: "Seats Filled",
  [TRIGGER_TYPE.SEATS_LEFT]: "Seats Left",
  [TRIGGER_TYPE.STOCK_SOLD]: "Stock Sold",
  [TRIGGER_TYPE.STOCK_LEFT]: "Stock Left",
  [TRIGGER_TYPE.MEMBER_COUNT]: "Member Count",
  [TRIGGER_TYPE.SPOTS_LEFT]: "Spots Left",
  [TRIGGER_TYPE.TIME_TO_SLOT]: "Time to Slot",
  [TRIGGER_TYPE.TIME_TO_EVENT]: "Time to Event",
  [TRIGGER_TYPE.TIME_TO_COHORT_START]: "Time to Cohort Start",
  [TRIGGER_TYPE.TIME_TO_BATCH_START]: "Time to Batch Start",
  [TRIGGER_TYPE.VIP_SLOTS]: "VIP / Priority Slots",
  [TRIGGER_TYPE.BATCH_WISE]: "Batch Wise",
};

// PRD §5 — single source of truth. Multi-type selection falls back to base two.
export const TRIGGER_TYPE_BY_OFFERING = {
  consultation: [
    TRIGGER_TYPE.SCHEDULE,
    TRIGGER_TYPE.EVERGREEN,
    TRIGGER_TYPE.SLOTS_FILLED,
    TRIGGER_TYPE.SLOTS_LEFT,
    TRIGGER_TYPE.TIME_TO_SLOT,
    TRIGGER_TYPE.VIP_SLOTS,
  ],
  course: [
    TRIGGER_TYPE.SCHEDULE,
    TRIGGER_TYPE.EVERGREEN,
    TRIGGER_TYPE.ENROLLMENT_FILLED,
    TRIGGER_TYPE.SEATS_LEFT,
    TRIGGER_TYPE.TIME_TO_COHORT_START,
  ],
  event: [
    TRIGGER_TYPE.SCHEDULE,
    TRIGGER_TYPE.EVERGREEN,
    TRIGGER_TYPE.SEATS_FILLED,
    TRIGGER_TYPE.SEATS_LEFT,
    TRIGGER_TYPE.TIME_TO_EVENT,
  ],
  community: [
    TRIGGER_TYPE.SCHEDULE,
    TRIGGER_TYPE.EVERGREEN,
    TRIGGER_TYPE.MEMBER_COUNT,
    // TODO: picks up only when communities expose a target size — until then
    // the BE trigger-config intersection (filterTriggersByServer) filters it.
    TRIGGER_TYPE.SPOTS_LEFT,
    TRIGGER_TYPE.TIME_TO_COHORT_START,
  ],
  batch: [
    TRIGGER_TYPE.SCHEDULE,
    TRIGGER_TYPE.EVERGREEN,
    TRIGGER_TYPE.SEATS_FILLED,
    TRIGGER_TYPE.SEATS_LEFT,
    TRIGGER_TYPE.TIME_TO_BATCH_START,
    TRIGGER_TYPE.BATCH_WISE,
  ],
  merchandise: [
    TRIGGER_TYPE.SCHEDULE,
    TRIGGER_TYPE.EVERGREEN,
    TRIGGER_TYPE.STOCK_SOLD,
    TRIGGER_TYPE.STOCK_LEFT,
  ],
};

export const BASE_TRIGGER_TYPES = [
  TRIGGER_TYPE.SCHEDULE,
  TRIGGER_TYPE.EVERGREEN,
];

export const TIERED_TRIGGER_TYPES = [
  TRIGGER_TYPE.SLOTS_FILLED,
  TRIGGER_TYPE.SLOTS_LEFT,
  TRIGGER_TYPE.ENROLLMENT_FILLED,
  TRIGGER_TYPE.SEATS_FILLED,
  TRIGGER_TYPE.SEATS_LEFT,
  TRIGGER_TYPE.SPOTS_LEFT,
  TRIGGER_TYPE.STOCK_SOLD,
  TRIGGER_TYPE.STOCK_LEFT,
  TRIGGER_TYPE.MEMBER_COUNT,
];

export const TIME_PROXIMITY_TRIGGER_TYPES = [
  TRIGGER_TYPE.TIME_TO_SLOT,
  TRIGGER_TYPE.TIME_TO_EVENT,
  TRIGGER_TYPE.TIME_TO_COHORT_START,
  TRIGGER_TYPE.TIME_TO_BATCH_START,
];

export const COUNT_DOWN_TRIGGERS = [
  TRIGGER_TYPE.SLOTS_LEFT,
  TRIGGER_TYPE.SEATS_LEFT,
  TRIGGER_TYPE.SPOTS_LEFT,
  TRIGGER_TYPE.STOCK_LEFT,
];

export const SELECTION_METHOD = {
  LOWEST: "lowest",
  HIGHEST: "highest",
  RECENT: "recent",
};

export const SELECTION_METHOD_OPTIONS = [
  {
    label: "Give the customer the lowest price",
    description:
      "Customer gets whichever applicable rule is cheapest.",
    value: SELECTION_METHOD.LOWEST,
  },
  {
    label: "Give the customer the highest price",
    description: "Surge wins over discount if both could apply.",
    value: SELECTION_METHOD.HIGHEST,
  },
  {
    label: "Use whichever one I set up most recently",
    description: "Newest rule wins over older ones, regardless of price.",
    value: SELECTION_METHOD.RECENT,
  },
];

export const UNIFIED_OFFER_ENABLED = true;

/**
 * Per-trigger tier presentation (mirrors the approved mockup's triggerMeta).
 * metricLabel: left column header. defaultThresholdUnit: initial unit toggle.
 * numberSuffix: count-unit toggle label. countdown: descending thresholds.
 */
export const TRIGGER_TIER_META = {
  [TRIGGER_TYPE.SLOTS_FILLED]: {
    metricLabel: "SLOTS FILLED REACHES",
    defaultThresholdUnit: THRESHOLD_UNIT.PERCENT,
    numberSuffix: "#",
    countdown: false,
  },
  [TRIGGER_TYPE.SLOTS_LEFT]: {
    metricLabel: "SLOTS LEFT DROPS TO",
    defaultThresholdUnit: THRESHOLD_UNIT.COUNT,
    numberSuffix: "#",
    countdown: true,
  },
  [TRIGGER_TYPE.ENROLLMENT_FILLED]: {
    metricLabel: "ENROLLMENT REACHES",
    defaultThresholdUnit: THRESHOLD_UNIT.PERCENT,
    numberSuffix: "# seats",
    countdown: false,
  },
  [TRIGGER_TYPE.SEATS_FILLED]: {
    metricLabel: "SEATS FILLED REACHES",
    defaultThresholdUnit: THRESHOLD_UNIT.PERCENT,
    numberSuffix: "# seats",
    countdown: false,
  },
  [TRIGGER_TYPE.SEATS_LEFT]: {
    metricLabel: "SEATS LEFT DROPS TO",
    defaultThresholdUnit: THRESHOLD_UNIT.COUNT,
    numberSuffix: "# seats",
    countdown: true,
  },
  [TRIGGER_TYPE.SPOTS_LEFT]: {
    metricLabel: "SPOTS LEFT DROPS TO",
    defaultThresholdUnit: THRESHOLD_UNIT.COUNT,
    numberSuffix: "#",
    countdown: true,
  },
  [TRIGGER_TYPE.STOCK_SOLD]: {
    metricLabel: "STOCK SOLD REACHES",
    defaultThresholdUnit: THRESHOLD_UNIT.PERCENT,
    numberSuffix: "# units",
    countdown: false,
  },
  [TRIGGER_TYPE.STOCK_LEFT]: {
    metricLabel: "STOCK LEFT DROPS TO",
    defaultThresholdUnit: THRESHOLD_UNIT.PERCENT,
    numberSuffix: "# units",
    countdown: true,
  },
  [TRIGGER_TYPE.MEMBER_COUNT]: {
    metricLabel: "MEMBERS REACH",
    defaultThresholdUnit: THRESHOLD_UNIT.COUNT,
    numberSuffix: "",
    countdown: false,
    absoluteOnly: true,
  },
  [TRIGGER_TYPE.TIME_TO_SLOT]: { metricLabel: "TIME BEFORE APPOINTMENT" },
  [TRIGGER_TYPE.TIME_TO_EVENT]: { metricLabel: "TIME BEFORE EVENT" },
  [TRIGGER_TYPE.TIME_TO_COHORT_START]: {
    metricLabel: "TIME BEFORE COHORT STARTS",
  },
  [TRIGGER_TYPE.TIME_TO_BATCH_START]: {
    metricLabel: "TIME BEFORE BATCH STARTS",
  },
};

/** Sensible default trigger per offering family (mockup behavior). */
export const DEFAULT_TRIGGER_BY_FAMILY = {
  consultation: TRIGGER_TYPE.VIP_SLOTS,
  course: TRIGGER_TYPE.ENROLLMENT_FILLED,
  event: TRIGGER_TYPE.SEATS_FILLED,
  community: TRIGGER_TYPE.MEMBER_COUNT,
  batch: TRIGGER_TYPE.SEATS_LEFT,
  merchandise: TRIGGER_TYPE.STOCK_LEFT,
};

export const EVERGREEN_START_OPTIONS = [
  {
    label: "Customer views the offering page",
    value: EVERGREEN_START_EVENT.PAGE_VIEW,
  },
  {
    label: "Customer joins the waitlist",
    value: EVERGREEN_START_EVENT.WAITLIST,
  },
  { label: "Customer signs up / enrolls", value: EVERGREEN_START_EVENT.SIGNUP },
];

/**
 * Default visibility of the offer at checkout (§4.6): on for Early Bird
 * (discount), off for Surge. Creators can override per offer.
 */
export const getDefaultShowAtCheckout = (direction) =>
  direction !== OFFER_DIRECTION.SURGE;

export const FAMILY_LABELS = {
  consultation: "1-1 Consultations",
  course: "Course",
  event: "Live Events",
  community: "Community",
  batch: "Batch Programs",
  merchandise: "Merchandise",
};

const OFFERING_START_KEYS = [
  "start_date",
  "start_datetime",
  "class_start_date",
  "batch_start_date",
  "event_start_date",
  "cohort_start_date",
  "schedule_start_date",
];

/**
 * Best-effort offering start date (YYYY-MM-DD) for sync-to-start (§4.3).
 * Returns null when the offering has no scheduled start — the checkbox is
 * then hidden per §6. Tolerant of date/datetime strings and Date objects.
 */
export const getOfferingStartDate = (selectedListings = []) => {
  for (const listing of selectedListings) {
    if (!listing) continue;
    for (const key of OFFERING_START_KEYS) {
      const raw = listing[key];
      if (!raw) continue;
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) continue;
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    }
  }
  return null;
};

export const isTieredTrigger = (t) => TIERED_TRIGGER_TYPES.includes(t);
export const isTimeProximityTrigger = (t) =>
  TIME_PROXIMITY_TRIGGER_TYPES.includes(t);
export const isScheduleTrigger = (t) => t === TRIGGER_TYPE.SCHEDULE;
export const isEvergreenTrigger = (t) => t === TRIGGER_TYPE.EVERGREEN;

/**
 * Infer offering family from a listing payload. The offerings list returns a
 * numeric `type` (schedule type id) in most cases, but some callers pass a
 * string `listing_type`, so handle both (and never call .toLowerCase() on a
 * number).
 */
export const NUMERIC_FAMILY_BY_TYPE = {
  [schedule_types_ids.one_on_one_appointment]: "consultation",
  [schedule_types_ids.one_on_one_class]: "batch",
  [schedule_types_ids.group_class]: "batch",
  [schedule_types_ids.workshop]: "event",
  [schedule_types_ids.content]: "course",
  [schedule_types_ids.merchandise]: "merchandise",
  [schedule_types_ids.rolling_class]: "batch",
  [schedule_types_ids.telegram]: "community",
  [schedule_types_ids.webinar]: "event",
  [schedule_types_ids.whatsapp]: "community",
  [schedule_types_ids.branded_community]: "community",
};

/**
 * Quick Payment Pages (schedule type `no_schedule`) have no schedule, so
 * sync-to-start never applies to them — even if some datetime leaks in from
 * the listing payload or trigger config. Never call .toLowerCase() on a
 * number (0 is a valid schedule-type id, so don't use ||).
 */
export const isUnscheduledOffering = (listing) => {
  if (!listing) return false;
  const raw = [listing.listing_type, listing.type, listing.offering_type].find(
    (v) => v !== undefined && v !== null && v !== ""
  );
  if (raw === undefined) return false;
  if (typeof raw === "number" || /^\d+$/.test(String(raw))) {
    return Number(raw) === schedule_types_ids.no_schedule;
  }
  const type = String(raw).toLowerCase();
  return type.includes("quick payment") || type.includes("no_schedule");
};

/**
 * Never drop an existing offer's saved trigger from the dropdown options:
 * the server allow-list can omit it for the listing, leaving the locked
 * edit dropdown empty (and the update payload drifting → backend 400).
 * Creates always pick from valid options, so no injection there.
 */
export const withSavedTrigger = (triggers = [], savedTrigger, isEdit) => {
  if (isEdit && savedTrigger && !triggers.includes(savedTrigger)) {
    return [...triggers, savedTrigger];
  }
  return triggers;
};

export const inferOfferingFamily = (listing) => {
  if (!listing) return null;
  // Pick the first present value (0 is a valid schedule-type id, so don't use ||).
  const raw = [listing.listing_type, listing.type, listing.offering_type].find(
    (v) => v !== undefined && v !== null && v !== ""
  );
  if (raw === undefined) return null;
  // Numeric schedule-type id (or a numeric string).
  if (typeof raw === "number" || /^\d+$/.test(String(raw))) {
    return NUMERIC_FAMILY_BY_TYPE[Number(raw)] || null;
  }
  const type = String(raw).toLowerCase();
  if (type.includes("consult") || type.includes("appointment"))
    return "consultation";
  if (type.includes("cours")) return "course";
  if (
    type.includes("event") ||
    type.includes("webinar") ||
    type.includes("workshop")
  )
    return "event";
  if (type.includes("commun")) return "community";
  if (type.includes("batch") || type.includes("class")) return "batch";
  if (type.includes("merch") || type.includes("product")) return "merchandise";
  return null;
};

export const getAvailableTriggers = (selectedListings = []) => {
  if (!selectedListings.length) return Object.values(TRIGGER_TYPE);
  const families = [
    ...new Set(selectedListings.map(inferOfferingFamily).filter(Boolean)),
  ];
  // Unknown family or mixed families → only common triggers.
  if (families.length !== 1) return [...BASE_TRIGGER_TYPES];
  return TRIGGER_TYPE_BY_OFFERING[families[0]] || [...BASE_TRIGGER_TYPES];
};

/**
 * Trigger options for the current target selection.
 * - Universal (all offerings) → only the common triggers (backend allows
 *   just schedule/evergreen off a specific offering).
 * - Specific offering(s) → filtered by the selected offering family/ies.
 * - Payment page(s) → only the common triggers (offering types unknown).
 * - Nothing selected yet → [] (the Trigger Type section is hidden).
 * - VIP / Batch Wise need exactly one offering (backend restriction).
 */
export const getTriggerOptionsForTarget = ({
  isUniversal = false,
  isPaymentPage = false,
  hasPaymentPages = false,
  selectedOfferings = [],
} = {}) => {
  if (isUniversal) return [...BASE_TRIGGER_TYPES];
  if (isPaymentPage) return hasPaymentPages ? [...BASE_TRIGGER_TYPES] : [];
  if (!selectedOfferings.length) return [];
  const options = getAvailableTriggers(selectedOfferings);
  if (selectedOfferings.length !== 1) {
    return options.filter(
      (t) => t !== TRIGGER_TYPE.VIP_SLOTS && t !== TRIGGER_TYPE.BATCH_WISE
    );
  }
  return options;
};

/**
 * Legacy → unified. Existing `lto_data / early_bird_expiry` becomes a
 * discount schedule/evergreen rule with show_at_checkout=true.
 */
export const parseUnifiedOffer = (apiPayload = {}) => {
  if (apiPayload?.direction && apiPayload?.trigger_type) return apiPayload;
  const metadata = apiPayload?.metadata || apiPayload?.lto_data || {};
  const timerType = metadata.timer_type;
  return {
    direction: apiPayload?.direction || OFFER_DIRECTION.DISCOUNT,
    trigger_type:
      timerType === 2 ? TRIGGER_TYPE.EVERGREEN : TRIGGER_TYPE.SCHEDULE,
    tiers: apiPayload?.tiers || metadata?.tiers || [],
    show_at_checkout:
      apiPayload?.show_at_checkout ?? metadata?.show_at_checkout ?? true,
    sync_end_to_start: false,
    selection_method: SELECTION_METHOD.LOWEST,
  };
};
/** New keys ride inside `metadata` so current backend safely ignores them. */
export const buildUnifiedMetadata = (values = {}) => ({
  direction: values.direction || OFFER_DIRECTION.DISCOUNT,
  trigger_type: values.trigger_type || TRIGGER_TYPE.SCHEDULE,
  tiers: values.tiers || [],
  show_at_checkout: values.show_at_checkout ?? true,
  sync_end_to_start: values.sync_end_to_start ?? false,
  threshold_unit: values.threshold_unit || DEFAULT_THRESHOLD_UNIT,
  value_unit: values.value_unit || DEFAULT_VALUE_UNIT,
  evergreen_start_event:
    values.evergreen_start_event || DEFAULT_EVERGREEN_START_EVENT,
  vip_apply_by: values.vip_apply_by || DEFAULT_VIP_APPLY_BY,
  vip_windows: values.vip_windows || [],
  vip_start_date: values.vip_start_date || "",
  vip_end_date: values.vip_end_date || "",
  batch_values: values.batch_values || [],
});

/**
 * Direction-aware labels for a transaction/booking's applied offer
 * (`feature_discount_info`). Legacy payloads have no direction → treated as a
 * discount (Early Bird), preserving existing behaviour. Used by the
 * transactions table + detail modal so Surge rules render the same way.
 */
export const OFFER_TYPE_LABEL = {
  [OFFER_DIRECTION.DISCOUNT]: "Early Bird",
  [OFFER_DIRECTION.SURGE]: "Surge",
};

export const parseTransactionOffer = (offer = {}) => {
  const direction =
    offer?.direction || offer?.metadata?.direction || OFFER_DIRECTION.DISCOUNT;
  const isSurge = direction === OFFER_DIRECTION.SURGE;
  return {
    direction,
    isSurge,
    title: offer?.code || offer?.title || offer?.text || "",
    typeLabel:
      OFFER_TYPE_LABEL[direction] || OFFER_TYPE_LABEL[OFFER_DIRECTION.DISCOUNT],
    valueLabel: isSurge ? "Surge value" : "Discount value",
    // Modal title: keep the existing umbrella for discount, name Surge explicitly.
    modalTitle: isSurge ? "Surge Pricing" : "Limited-Time Offer",
    triggerType: offer?.metadata?.trigger_type || offer?.trigger_type || "",
    value: offer?.value,
  };
};
